import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, Trash2, AlertCircle, CheckCircle2, Copy, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { calculateSteelArea, calculateAutoSteelGroutBond, getSteelGroutBond } from '@/lib/calculations';

export const PileMakeupTab = ({ pileInputs, setPileInputs, results, onCopyToAll, pileCount }) => {
  // Update a general parameter
  const updateParameter = (key, value) => {
    setPileInputs(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  // Update steel-grout bond mode
  const updateBondMode = (mode) => {
    setPileInputs(prev => ({
      ...prev,
      steelGroutBondMode: mode
    }));
  };

  // Update manual bond value
  const updateManualBond = (value) => {
    setPileInputs(prev => ({
      ...prev,
      steelGroutBondManual: parseFloat(value) || 0
    }));
  };

  // Update a pile section
  const updateSection = (id, key, value) => {
    setPileInputs(prev => ({
      ...prev,
      pileSections: prev.pileSections.map(section =>
        section.id === id ? { ...section, [key]: parseFloat(value) || 0 } : section
      )
    }));
  };

  // Add a new section
  const addSection = () => {
    const newId = Math.max(...pileInputs.pileSections.map(s => s.id), 0) + 1;
    setPileInputs(prev => ({
      ...prev,
      pileSections: [...prev.pileSections, { id: newId, diameter: 762, wallThickness: 25, length: 10 }]
    }));
  };

  // Remove a section
  const removeSection = (id) => {
    if (pileInputs.pileSections.length <= 1) return;
    setPileInputs(prev => ({
      ...prev,
      pileSections: prev.pileSections.filter(s => s.id !== id)
    }));
  };

  // Calculate totals
  const totalSteelArea = pileInputs.pileSections.reduce((sum, s) => 
    sum + calculateSteelArea(s.diameter, s.wallThickness), 0
  );
  const totalLength = pileInputs.pileSections.reduce((sum, s) => sum + s.length, 0);

  // Get current bond mode and values
  const bondMode = pileInputs.steelGroutBondMode || 'manual';
  const autoSteelGroutBond = calculateAutoSteelGroutBond(pileInputs.groutStrength);
  const effectiveSteelGroutBond = getSteelGroutBond(pileInputs);

  // Steel-grout preview calculations
  const avgDiameter = pileInputs.pileSections.length > 0
    ? pileInputs.pileSections.reduce((sum, s) => sum + s.diameter * s.length, 0) / totalLength
    : 762;
  
  const steelCapacity = (totalSteelArea * 1e-6) * (pileInputs.steelYieldStrength * 1000) / 1000;
  const steelGroutCapacity = Math.PI * (avgDiameter / 1000) * pileInputs.groutLength * effectiveSteelGroutBond;
  const governingCapacity = Math.min(steelCapacity, steelGroutCapacity);
  const governedBySteel = steelCapacity <= steelGroutCapacity;

  return (
    <div className="space-y-6" data-testid="pile-makeup-tab">
      {/* Copy to All Piles Button */}
      {pileCount > 1 && onCopyToAll && (
        <Card className="border-dashed border-amber-500/50 bg-amber-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">Apply to all piles</span>
                <span className="text-muted-foreground ml-2">Copy current pile makeup settings to all {pileCount - 1} other piles</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onCopyToAll}
                className="border-amber-500 hover:bg-amber-500/10"
                data-testid="copy-pile-makeup-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy to All Piles
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* General Parameters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">General Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="form-group">
              <Label htmlFor="steelYieldStrength" className="form-label">
                Steel Yield Strength (MPa)
              </Label>
              <Input
                id="steelYieldStrength"
                type="number"
                value={pileInputs.steelYieldStrength}
                onChange={(e) => updateParameter('steelYieldStrength', e.target.value)}
                className="engineering-input"
                data-testid="input-steel-yield"
              />
              <p className="form-hint">f<sub>y</sub> - Typical: 355 MPa</p>
            </div>

            <div className="form-group">
              <Label htmlFor="groutLength" className="form-label">
                Grout Length (m)
              </Label>
              <Input
                id="groutLength"
                type="number"
                step="0.1"
                value={pileInputs.groutLength}
                onChange={(e) => updateParameter('groutLength', e.target.value)}
                className="engineering-input"
                data-testid="input-grout-length"
              />
              <p className="form-hint">L<sub>grout</sub> - Grouted annulus length</p>
            </div>

            <div className="form-group">
              <Label htmlFor="groutStrength" className="form-label">
                Grout Strength (MPa)
              </Label>
              <Input
                id="groutStrength"
                type="number"
                value={pileInputs.groutStrength}
                onChange={(e) => updateParameter('groutStrength', e.target.value)}
                className="engineering-input"
                data-testid="input-grout-strength"
              />
              <p className="form-hint">f<sub>c</sub> - 28-day compressive</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steel-Grout Bond Configuration */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Steel-Grout Bond T<sub>sg</sub>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    Auto mode: T<sub>sg</sub> = 0.03 × f<sub>c</sub>, capped at 1.5 MPa.
                    Manual mode allows you to input project-specific bond test values.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup 
            value={bondMode} 
            onValueChange={updateBondMode}
            className="space-y-4"
          >
            {/* Auto Mode */}
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="auto" id="bond-auto" className="mt-1" data-testid="bond-mode-auto" />
              <div className="flex-1">
                <Label htmlFor="bond-auto" className="font-medium cursor-pointer">
                  Auto from grout strength
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  T<sub>sg</sub> = 0.03 × f<sub>c</sub> = 0.03 × {pileInputs.groutStrength} = {autoSteelGroutBond.toFixed(2)} MPa
                  {autoSteelGroutBond >= 1.5 && <span className="text-amber-600"> (capped at 1.5 MPa)</span>}
                </p>
                {bondMode === 'auto' && (
                  <div className="mt-2 p-3 rounded-lg bg-[#00284F]/10 border border-[#00284F]/20">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Steel-Grout Bond (auto)</span>
                      <span className="font-mono font-bold text-[#00284F] dark:text-blue-400">
                        {autoSteelGroutBond.toFixed(2)} MPa
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Manual Mode */}
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="manual" id="bond-manual" className="mt-1" data-testid="bond-mode-manual" />
              <div className="flex-1">
                <Label htmlFor="bond-manual" className="font-medium cursor-pointer">
                  Manual steel-grout bond
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Enter project-specific bond value from testing
                </p>
                {bondMode === 'manual' && (
                  <div className="mt-2">
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        step="0.01"
                        value={pileInputs.steelGroutBondManual || 0}
                        onChange={(e) => updateManualBond(e.target.value)}
                        className="w-32 engineering-input"
                        data-testid="input-steel-grout-bond-manual"
                      />
                      <span className="text-sm text-muted-foreground">MPa</span>
                    </div>
                    <p className="form-hint mt-1">T<sub>sg</sub> - Steel-grout interface bond</p>
                  </div>
                )}
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Pile Sections */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Pile Sections</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={addSection}
            data-testid="add-section-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Section
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Section</TableHead>
                  <TableHead className="text-right">Diameter (mm)</TableHead>
                  <TableHead className="text-right">Wall (mm)</TableHead>
                  <TableHead className="text-right">Length (m)</TableHead>
                  <TableHead className="text-right">Steel Area (mm²)</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pileInputs.pileSections.map((section, index) => {
                  const area = calculateSteelArea(section.diameter, section.wallThickness);
                  return (
                    <TableRow key={section.id} data-testid={`pile-section-row-${index}`}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="text-right p-1">
                        <Input
                          type="number"
                          value={section.diameter}
                          onChange={(e) => updateSection(section.id, 'diameter', e.target.value)}
                          className="w-24 ml-auto engineering-input"
                          data-testid={`input-section-diameter-${index}`}
                        />
                      </TableCell>
                      <TableCell className="text-right p-1">
                        <Input
                          type="number"
                          value={section.wallThickness}
                          onChange={(e) => updateSection(section.id, 'wallThickness', e.target.value)}
                          className="w-20 ml-auto engineering-input"
                          data-testid={`input-section-wall-${index}`}
                        />
                      </TableCell>
                      <TableCell className="text-right p-1">
                        <Input
                          type="number"
                          step="0.1"
                          value={section.length}
                          onChange={(e) => updateSection(section.id, 'length', e.target.value)}
                          className="w-20 ml-auto engineering-input"
                          data-testid={`input-section-length-${index}`}
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {area.toFixed(0)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSection(section.id)}
                          disabled={pileInputs.pileSections.length <= 1}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-section-btn-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                <TableRow className="bg-muted/50 font-semibold">
                  <TableCell colSpan={3} className="text-right">Total</TableCell>
                  <TableCell className="text-right font-mono">{totalLength.toFixed(1)}</TableCell>
                  <TableCell className="text-right font-mono" data-testid="total-steel-area">
                    {totalSteelArea.toFixed(0)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Steel-Grout Preview Card */}
      <Card className="border-[#00284F]/20 bg-[#00284F]/5 dark:bg-[#00284F]/10">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-[#00284F]" />
            Steel-Grout Connection Capacity Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Active T_sg display */}
          <div className="mb-4 p-3 rounded-lg bg-muted/50 border">
            <div className="flex items-center justify-between">
              <span className="text-sm">
                T<sub>sg</sub> = {effectiveSteelGroutBond.toFixed(2)} MPa
                <span className="text-muted-foreground ml-2">
                  ({bondMode === 'auto' 
                    ? `auto from f_c = ${pileInputs.groutStrength} MPa` 
                    : 'manual'})
                </span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Steel Capacity */}
            <div 
              className={`p-4 rounded-lg border ${governedBySteel ? 'border-[#00284F] bg-[#00284F]/10' : 'border-border bg-card'}`}
              data-testid="steel-capacity-card"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Steel Capacity</span>
                {governedBySteel && (
                  <span className="text-xs bg-[#00284F] text-white px-2 py-0.5 rounded">GOVERNING</span>
                )}
              </div>
              <div className="text-2xl font-bold font-mono">
                {steelCapacity.toFixed(3)} <span className="text-sm font-normal">MN</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Q<sub>steel</sub> = A<sub>s</sub> × f<sub>y</sub>
              </div>
            </div>

            {/* Grout-Bond Capacity */}
            <div 
              className={`p-4 rounded-lg border ${!governedBySteel ? 'border-[#00284F] bg-[#00284F]/10' : 'border-border bg-card'}`}
              data-testid="grout-bond-capacity-card"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Grout-Bond Capacity</span>
                {!governedBySteel && (
                  <span className="text-xs bg-[#00284F] text-white px-2 py-0.5 rounded">GOVERNING</span>
                )}
              </div>
              <div className="text-2xl font-bold font-mono">
                {steelGroutCapacity.toFixed(3)} <span className="text-sm font-normal">MN</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Q<sub>sg</sub> = πD × L<sub>grout</sub> × T<sub>sg</sub>
              </div>
            </div>

            {/* Governing Value */}
            <div className="p-4 rounded-lg bg-[#00284F] text-white" data-testid="governing-capacity-card">
              <div className="text-sm opacity-80 mb-2">Steel-Grout Connection</div>
              <div className="text-3xl font-bold font-mono">
                {governingCapacity.toFixed(3)} <span className="text-sm font-normal">MN</span>
              </div>
              <div className="text-xs opacity-70 mt-1">
                min(Q<sub>steel</sub>, Q<sub>sg</sub>)
              </div>
            </div>
          </div>

          {/* Warning if steel is governing */}
          {governedBySteel && (
            <div className="mt-4 flex items-start gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/20">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-700 dark:text-amber-400">Steel capacity is governing</p>
                <p className="text-muted-foreground mt-0.5">
                  Consider increasing wall thickness or using higher grade steel to improve efficiency.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
